<template>
  <section ref="bar" class="service-shortcuts" aria-label="快捷操作">
    <div class="shortcut-main">
      <span class="shortcut-label">快捷操作</span>
      <span v-if="managerVisible && hasDraftChanges" class="shortcut-preview-hint" role="status">预览 · 未保存</span>
      <span v-if="initialLoading" class="shortcut-hint" role="status">正在加载…</span>
      <template v-else>
        <div ref="links" class="shortcut-links">
          <span v-for="entry in visibleEntries" :key="entry.menuCode" class="shortcut-entry">
            <el-dropdown v-if="entry.childrenList.length" trigger="click" @command="navigate">
              <el-button size="small" :type="isActive(entry) ? 'primary' : 'default'" :title="entry.menuPath">
                {{ shortcutName(entry) }} <i class="el-icon-arrow-down" aria-hidden="true"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item v-for="child in entry.childrenList" :key="child.menuCode" :command="child.menuCode"
                                  :class="{ 'shortcut-current': activeCode === child.menuCode }">{{ child.menuName }}</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
            <el-button v-else size="small" :type="isActive(entry) ? 'primary' : 'default'" :title="entry.menuPath"
                       :aria-current="isActive(entry) ? 'page' : null" @click="navigate(entry.menuCode)">{{ shortcutName(entry) }}</el-button>
          </span>
          <el-dropdown v-if="overflowEntries.length" trigger="click" @command="navigate">
            <el-button size="small">更多 <i class="el-icon-arrow-down" aria-hidden="true"></i></el-button>
            <el-dropdown-menu slot="dropdown">
              <template v-for="entry in overflowEntries">
                <template v-if="entry.childrenList.length">
                  <el-dropdown-item :key="entry.menuCode" disabled>{{ entry.menuName }}</el-dropdown-item>
                  <el-dropdown-item v-for="child in entry.childrenList" :key="entry.menuCode + ':' + child.menuCode"
                                    :command="child.menuCode" :class="{ 'shortcut-current': activeCode === child.menuCode }">{{ child.menuPath }}</el-dropdown-item>
                </template>
                <el-dropdown-item v-else :key="entry.menuCode" :command="entry.menuCode"
                                  :class="{ 'shortcut-current': isActive(entry) }">{{ entry.menuPath }}</el-dropdown-item>
              </template>
            </el-dropdown-menu>
          </el-dropdown>
          <span v-if="nav.menuReady && !entries.length" class="shortcut-hint">暂无快捷菜单，可从右侧添加。</span>
        </div>
      </template>
      <el-popover v-model="addVisible" placement="bottom-end" width="360" trigger="click" popper-class="service-shortcut-picker">
        <div v-loading="nav.saving || nav.configLoading || nav.menuLoading" :aria-busy="nav.saving || nav.configLoading || nav.menuLoading">
          <h4 class="shortcut-picker-title">选择快捷菜单</h4>
          <fieldset class="shortcut-picker-fields" :disabled="nav.saving || nav.configLoading || nav.menuLoading">
            <el-input ref="menuSearch" v-model="filterText" size="small" placeholder="搜索一级、二级菜单" clearable aria-label="搜索快捷菜单" />
            <el-tree ref="menuTree" class="shortcut-tree" :data="candidates" node-key="menuCode" show-checkbox check-strictly
                     :props="treeProps" :filter-node-method="filterMenu" empty-text="暂无可添加的菜单">
              <template #default="scope">
                <span :title="scope.data.menuPath">{{ scope.data.menuName }}<small v-if="savedCodes.includes(scope.data.menuCode)" class="shortcut-added">已添加</small></span>
              </template>
            </el-tree>
          </fieldset>
          <p class="shortcut-picker-tip">一级目录作为下拉入口；二级菜单可单独添加。</p>
          <p v-if="editorError || nav.saveError" class="shortcut-error" role="alert">{{ editorError || nav.saveError }}</p>
          <p v-if="nav.configError || nav.menuError" class="shortcut-error" role="alert">
            {{ nav.menuError || nav.configError }} <el-button type="text" size="small" @click="reload">重新加载</el-button>
          </p>
          <div class="shortcut-picker-footer">
            <el-button type="text" size="small" :disabled="nav.saving" @click="openManager">管理排序</el-button>
            <div>
              <el-button size="small" :disabled="nav.saving" @click="addVisible = false">取消</el-button>
              <el-button size="small" type="primary" :loading="nav.saving" :disabled="!canEdit" @click="savePicker">保存</el-button>
            </div>
          </div>
        </div>
        <el-button slot="reference" class="shortcut-add" size="small" plain :disabled="!canEdit">
          添加菜单 <i class="el-icon-arrow-down" aria-hidden="true"></i>
        </el-button>
      </el-popover>
      <el-button size="small" type="text" :disabled="!canEdit" @click="openManager">管理</el-button>
    </div>
    <div v-if="nav.configError || nav.menuError" class="shortcut-status" role="alert">
      <span>{{ nav.menuError || nav.configError }}</span>
      <el-button size="small" type="text" :loading="nav.menuLoading || nav.configLoading" @click="reload">重新加载</el-button>
    </div>

    <!-- 按真实按钮样式测量所有入口；不占布局空间，也不进入键盘/读屏导航。 -->
    <div class="shortcut-measure" aria-hidden="true">
      <div ref="measureRow" class="shortcut-measure-row">
        <span v-for="entry in entries" :key="entry.menuCode" ref="entryMeasure" class="shortcut-entry" :data-menu-code="entry.menuCode">
          <el-button size="small" :type="isActive(entry) ? 'primary' : 'default'" tabindex="-1">
            {{ shortcutName(entry) }}<template v-if="entry.childrenList.length"> <i class="el-icon-arrow-down"></i></template>
          </el-button>
        </span>
        <el-button ref="moreMeasure" size="small" tabindex="-1">更多 <i class="el-icon-arrow-down"></i></el-button>
      </div>
    </div>

    <el-dialog title="管理快捷菜单" :visible.sync="managerVisible" width="620px" append-to-body
               custom-class="service-shortcut-manager" :close-on-click-modal="false" :before-close="beforeCloseManager">
      <p class="shortcut-picker-tip">调整顺序或移除后，上方快捷栏实时预览；点击保存后生效。取消或关闭将恢复原设置，不影响左侧菜单。</p>
      <ul v-if="draftCodes.length" class="shortcut-sort-list">
        <li v-for="(code, index) in draftCodes" :key="code">
          <span class="shortcut-sort-name">{{ candidateIndex[code].menuPath }}</span>
          <div class="shortcut-sort-actions">
            <el-button size="mini" icon="el-icon-arrow-up" :disabled="nav.saving || index === 0" :aria-label="'上移：' + candidateIndex[code].menuName" @click="move(index, -1)" />
            <el-button size="mini" icon="el-icon-arrow-down" :disabled="nav.saving || index === draftCodes.length - 1" :aria-label="'下移：' + candidateIndex[code].menuName" @click="move(index, 1)" />
            <el-button size="mini" type="text" :disabled="nav.saving" :aria-label="'移除：' + candidateIndex[code].menuName" @click="draftCodes.splice(index, 1)">移除</el-button>
          </div>
        </li>
      </ul>
      <p v-else class="shortcut-hint">保存后只显示添加入口，可随时重新添加。</p>
      <p v-if="editorError || nav.saveError" class="shortcut-error" role="alert">{{ editorError || nav.saveError }}</p>
      <p v-if="nav.configError || nav.menuError" class="shortcut-error" role="alert">
        {{ nav.menuError || nav.configError }} <el-button type="text" size="small" :loading="nav.configLoading || nav.menuLoading" @click="reload">重新加载</el-button>
      </p>
      <template #footer>
        <div class="shortcut-manager-footer">
          <el-button plain :disabled="nav.saving || !canEdit" @click="restoreDefaults">恢复默认</el-button>
          <div>
            <el-button :disabled="nav.saving" @click="managerVisible = false">取消</el-button>
            <el-button type="primary" :loading="nav.saving" :disabled="!canEdit" @click="saveCodes(draftCodes)">保存</el-button>
          </div>
        </div>
      </template>
    </el-dialog>
  </section>
</template>

<script>
import { shortcutCandidates, flattenServiceMenus, activeServiceMenu, activateServiceMenu, shortcutName } from '@/utils/serviceMenus'

export default {
  name: 'ServiceShortcuts',
  data() {
    return {
      addVisible: false, managerVisible: false, filterText: '', draftCodes: [], editorError: '',
      linksWidth: 0, entryWidths: {}, moreWidth: 0, linksGap: 8,
      treeProps: { children: 'childrenList', label: 'menuName' }
    }
  },
  computed: {
    nav() { return this.$store.state.serviceNavigation },
    candidates() { return shortcutCandidates(this.nav.menus, this.$router) },
    candidateIndex() {
      const index = Object.create(null)
      flattenServiceMenus(this.candidates).forEach(menu => { index[menu.menuCode] = menu })
      return index
    },
    savedCodes() { return this.$store.getters['serviceNavigation/shortcutCodes'].filter(code => this.candidateIndex[code]) },
    hasDraftChanges() {
      return this.draftCodes.length !== this.savedCodes.length || this.draftCodes.some((code, index) => code !== this.savedCodes[index])
    },
    displayCodes() { return this.managerVisible ? this.draftCodes.filter(code => this.candidateIndex[code]) : this.savedCodes },
    entries() { return this.displayCodes.map(code => this.candidateIndex[code]) },
    visibleCount() {
      if (!this.entries.length || !this.linksWidth) return 0
      const widths = this.entries.map(entry => this.entryWidths[entry.menuCode])
      if (widths.some(width => !width)) return 0
      const available = Math.max(0, this.linksWidth - 2)
      const total = widths.reduce((sum, width) => sum + width, 0) + this.linksGap * (widths.length - 1)
      // 全部能放下时无需为“更多”预留空间。
      if (total <= available) return widths.length
      let used = 0
      let count = 0
      for (const width of widths) {
        const next = used + (count ? this.linksGap : 0) + width
        if (next + this.linksGap + this.moreWidth > available) break
        used = next
        count++
      }
      return count
    },
    visibleEntries() { return this.entries.slice(0, this.visibleCount) },
    overflowEntries() { return this.entries.slice(this.visibleCount) },
    activeCode() { const menu = activeServiceMenu(this.nav.menus, this.$route); return menu && menu.menuCode },
    canEdit() { return this.nav.configReady && this.nav.menuReady && !this.nav.saving && !this.nav.configLoading && !this.nav.menuLoading && !this.nav.menuError && !this.nav.configError },
    initialLoading() { return (!this.nav.menuReady && this.nav.menuLoading) || (!this.nav.configReady && this.nav.configLoading) },
    navigationEpoch() { return this.nav.epoch }
  },
  watch: {
    entries() { this.$nextTick(this.scheduleMeasureBar) },
    initialLoading() { this.$nextTick(this.scheduleMeasureBar) },
    managerVisible(value) {
      if (!value) { this.draftCodes = []; this.editorError = '' }
      this.$nextTick(this.scheduleMeasureBar)
    },
    candidateIndex(value) { this.draftCodes = this.draftCodes.filter(code => value[code]) },
    addVisible(value) {
      if (!value) return
      this.beginEdit()
      this.filterText = ''
      this.$nextTick(() => {
        if (this.$refs.menuTree) this.$refs.menuTree.setCheckedKeys(this.draftCodes)
        if (this.$refs.menuSearch) this.$refs.menuSearch.focus()
      })
    },
    filterText(value) { if (this.$refs.menuTree) this.$refs.menuTree.filter(value) },
    navigationEpoch() { this.addVisible = false; this.managerVisible = false; this.draftCodes = []; this.editorError = '' },
    '$route.fullPath'() { this.addVisible = false; this.managerVisible = false; this.$nextTick(this.scheduleMeasureBar) }
  },
  mounted() {
    this.$nextTick(this.scheduleMeasureBar)
    if (typeof ResizeObserver !== 'undefined') {
      this.barObserver = new ResizeObserver(this.scheduleMeasureBar)
      this.barObserver.observe(this.$refs.bar)
      this.barObserver.observe(this.$refs.measureRow)
    }
    window.addEventListener('resize', this.scheduleMeasureBar)
  },
  beforeDestroy() {
    if (this.barObserver) this.barObserver.disconnect()
    if (this.measureFrame) window.cancelAnimationFrame(this.measureFrame)
    window.removeEventListener('resize', this.scheduleMeasureBar)
  },
  methods: {
    shortcutName,
    isActive(entry) { return entry.menuCode === this.activeCode || entry.childrenList.some(child => child.menuCode === this.activeCode) },
    navigate(code) {
      const menu = this.candidateIndex[code]
      if (activateServiceMenu(this.$router, menu)) this.$store.commit('serviceNavigation/TOUCH_MENU')
    },
    scheduleMeasureBar() {
      if (this.measureFrame || this._isDestroyed) return
      this.measureFrame = window.requestAnimationFrame(() => {
        this.measureFrame = null
        this.measureBar()
      })
    },
    measureBar() {
      const links = this.$refs.links
      if (!links || this._isDestroyed) return
      // 此区域已扣除标题、添加和管理按钮的宽度，避免整体栏宽高估可用空间。
      if (this.barObserver && this.observedLinks !== links) {
        if (this.observedLinks) this.barObserver.unobserve(this.observedLinks)
        this.barObserver.observe(links)
        this.observedLinks = links
      }
      const style = window.getComputedStyle(links)
      const gap = parseFloat(style.columnGap)
      this.linksGap = Number.isFinite(gap) ? gap : 8
      this.linksWidth = links.clientWidth
      const widths = {}
      const refs = this.$refs.entryMeasure
      const probes = Array.isArray(refs) ? refs : refs ? [refs] : []
      probes.forEach(probe => {
        widths[probe.dataset.menuCode] = probe.offsetWidth
      })
      this.entryWidths = widths
      const more = this.$refs.moreMeasure
      this.moreWidth = more && more.$el ? more.$el.offsetWidth : 0
    },
    async reload() {
      if (this.nav.saving) return
      const epoch = this.navigationEpoch
      if (this.addVisible) this.draftCodes = this.pickerCodes()
      await Promise.all([
        this.$store.dispatch('serviceNavigation/loadMenus', true),
        this.$store.dispatch('serviceNavigation/loadConfig', true)
      ])
      if (epoch !== this.navigationEpoch) return
      this.$nextTick(() => {
        if (this.addVisible && this.$refs.menuTree) this.$refs.menuTree.setCheckedKeys(this.draftCodes)
      })
    },
    beginEdit() {
      this.draftCodes = this.savedCodes.slice()
      this.editorError = ''
      this.$store.commit('serviceNavigation/SET_SAVE_ERROR', '')
    },
    filterMenu(value, data) { return !value.trim() || data.menuPath.toLowerCase().includes(value.trim().toLowerCase()) },
    pickerCodes() {
      const checked = this.$refs.menuTree ? this.$refs.menuTree.getCheckedKeys().filter(code => this.candidateIndex[code]) : this.draftCodes
      return this.draftCodes.filter(code => checked.includes(code)).concat(checked.filter(code => !this.draftCodes.includes(code)))
    },
    openManager() {
      if (!this.canEdit || this.nav.saving) return
      if (this.addVisible) this.draftCodes = this.pickerCodes()
      else this.beginEdit()
      this.addVisible = false
      this.managerVisible = true
    },
    beforeCloseManager(done) { if (!this.nav.saving) done() },
    move(index, offset) {
      const next = index + offset
      if (this.nav.saving || next < 0 || next >= this.draftCodes.length) return
      const item = this.draftCodes.splice(index, 1)[0]
      this.draftCodes.splice(next, 0, item)
    },
    savePicker() { this.saveCodes(this.pickerCodes()) },
    async saveCodes(codes) {
      if (!this.canEdit || this.nav.saving) return
      this.editorError = ''
      const epoch = this.navigationEpoch
      const validCodes = codes.filter(code => this.candidateIndex[code])
      const saved = await this.$store.dispatch('serviceNavigation/persist', { codes: validCodes })
      if (epoch !== this.navigationEpoch) return
      if (saved) {
        this.addVisible = false
        this.managerVisible = false
        this.$nextTick(this.scheduleMeasureBar)
        this.$message.success('快捷菜单及显示顺序已保存')
      }
      else if (!this.nav.saveError) this.editorError = '保存失败，请重新加载设置后重试。'
    },
    async restoreDefaults() {
      if (!this.canEdit || this.nav.saving) return
      this.editorError = ''
      const epoch = this.navigationEpoch
      const saved = await this.$store.dispatch('serviceNavigation/persist', { reset: true })
      if (epoch !== this.navigationEpoch) return
      if (saved) { this.addVisible = false; this.managerVisible = false; this.$message.success('已恢复默认快捷菜单') }
      else if (!this.nav.saveError) this.editorError = '恢复默认失败，请重试。'
    }
  }
}
</script>

<style lang="scss" scoped>
.service-shortcuts { position: relative; margin-bottom: 16px; padding: 12px 16px; background: #fff; border: 1px solid #e4e4e4; border-radius: 8px; box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06); }
.shortcut-main, .shortcut-links { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; min-width: 0; }
.shortcut-links { flex: 1 1 0%; flex-wrap: nowrap; min-width: 100px; }
.shortcut-links > .el-dropdown { flex-shrink: 0; }
.shortcut-main > .shortcut-hint { flex: 1; }
.shortcut-label { flex-shrink: 0; white-space: nowrap; color: #606266; font-size: 14px; margin-right: 4px; }
.shortcut-preview-hint { flex-shrink: 0; color: #b88230; font-size: 12px; white-space: nowrap; }
.shortcut-entry { display: inline-flex; flex-shrink: 0; max-width: 200px; }
.shortcut-entry .el-button { max-width: 200px; overflow: hidden; text-overflow: ellipsis; }
.shortcut-entry .el-dropdown { max-width: 100%; }
.shortcut-measure { position: absolute; top: 0; left: 0; width: 0; height: 0; overflow: hidden; visibility: hidden; pointer-events: none; }
.shortcut-measure-row { display: inline-flex; align-items: center; gap: 8px; width: max-content; white-space: nowrap; }
.shortcut-add { flex-shrink: 0; }
.shortcut-hint { color: #606266; font-size: 13px; line-height: 1.6; }
.shortcut-status { display: flex; align-items: center; gap: 12px; font-size: 13px; color: #606266; margin-top: 8px; }
.shortcut-picker-title { margin: 0 0 12px; color: #303133; }
.shortcut-picker-fields { min-width: 0; border: 0; margin: 0; padding: 0; }
.shortcut-tree { margin: 12px 0; max-height: 320px; overflow: auto; }
.shortcut-added { margin-left: 12px; color: #606266; font-size: 12px; }
.shortcut-picker-tip { color: #606266; font-size: 13px; line-height: 1.6; margin: 0 0 12px; }
.shortcut-picker-footer, .shortcut-manager-footer { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.shortcut-error { margin: 12px 0; color: #b42318; line-height: 1.6; }
.shortcut-sort-list { list-style: none; margin: 0; padding: 0; }
.shortcut-sort-list li { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 0; border-bottom: 1px solid #ebeef5; }
.shortcut-sort-name { min-width: 0; overflow-wrap: anywhere; }
.shortcut-sort-actions { display: flex; flex-shrink: 0; align-items: center; }
@media (max-width: 600px) {
  .service-shortcuts { padding: 12px; }
  .shortcut-label { display: none; }
  .shortcut-sort-list li { flex-wrap: wrap; }
}
</style>

<style lang="scss">
.el-dropdown-menu__item.shortcut-current { color: #165dff; background: #ecf5ff; }
.el-popover.service-shortcut-picker { max-width: calc(100vw - 32px); box-sizing: border-box; }
.el-dialog.service-shortcut-manager { max-width: calc(100% - 32px); }
.service-shortcut-manager .el-dialog__body { max-height: 60vh; overflow-y: auto; }
</style>
